
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete'
import { Dropdown } from 'primereact/dropdown'
import { PersonApi, ShowApi, PersonSearchResultDto, HorseApi, HorseSearchResultDto, ExamApi, ExamDto, ExamParticipantDto, ShowStarterDto, StartersApi, ShowScoreEntryDto, ExamScheduleEntryDto } from '../../../api'
import { ShowDto } from '../../../types/ShowDto'
import { authState } from '../../../state/Auth.state'
import { StarterHistoryOverlay } from './StarterHistoryOverlay'
import { ExamParticipantsOverlay } from './ExamParticipantsOverlay'

type SortMode = 'place' | 'startingOrder'
interface OverlayTarget { registerId: string; mode: 'horse' | 'rider' | 'show'; name: string; showId?: number | null; showName?: string | null; horseRegisterId?: string | null; riderRegisterId?: string | null }

export const History = () => {
  /* STATE – Show */
  const [shows, setShows] = useState<ShowDto[]>([])
  const [selectedShow, setSelectedShow] = useState<ShowDto | null>(null)
  const [showsLoading, setShowsLoading] = useState(false)

  /* STATE – Exams */
  const [exams, setExams] = useState<ExamDto[]>([])
  const [selectedExam, setSelectedExam] = useState<ExamDto | null>(null)
  const [examsLoading, setExamsLoading] = useState(false)

  /* STATE – Teilnehmer */
  const [participants, setParticipants] = useState<ExamParticipantDto[]>([])
  const [participantsLoading, setParticipantsLoading] = useState(false)
  const [sortMode, setSortMode] = useState<SortMode>('startingOrder')
  const [overlay, setOverlay] = useState<OverlayTarget | null>(null)
  const [examOverlay, setExamOverlay] = useState<{ examId: number; examName?: string | null; showName?: string | null } | null>(null)

  const openOverlay = useCallback((target: OverlayTarget) => {
    window.history.pushState(null, '', window.location.href)
    setOverlay(target)
  }, [])

  const closeOverlay = useCallback(() => {
    setOverlay(null)
  }, [])

  useEffect(() => {
    if (!overlay) return
    const handler = () => setOverlay(null)
    window.addEventListener('popstate', handler)
    return () => window.removeEventListener('popstate', handler)
  }, [overlay])

  /* STATE – Alle Starter (wenn keine Prüfung gewählt) */
  const [allStarters, setAllStarters] = useState<ShowStarterDto[]>([])
  const [allStartersLoading, setAllStartersLoading] = useState(false)

  /* STATE – Prüfungsplan (examschedule) */
  const [scheduleTab, setScheduleTab] = useState<'exams' | 'starters'>('exams')
  const [examSchedule, setExamSchedule] = useState<ExamScheduleEntryDto[]>([])
  const [examScheduleLoading, setExamScheduleLoading] = useState(false)
  const [examScheduleSortCol, setExamScheduleSortCol] = useState<'nr_asc' | 'nr_desc' | 'zeit'>('zeit')

  /* STATE – Gesamtwertung */
  const [scores, setScores] = useState<ShowScoreEntryDto[]>([])
  const [scoresLoading, setScoresLoading] = useState(false)
  const [scoresType, setScoresType] = useState<number | null>(null)
  const [scoresExpanded, setScoresExpanded] = useState(false)

  /* STATE – Jahreswertung */
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: currentYear - 1997 }, (_, i) => currentYear - i)
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [yearScores, setYearScores] = useState<ShowScoreEntryDto[]>([])
  const [yearScoresLoading, setYearScoresLoading] = useState(false)
  const [yearScoresType, setYearScoresType] = useState<number | null>(null)
  const [yearScoresExpanded, setYearScoresExpanded] = useState(false)

  /* STATE – Person */
  const [personQuery, setPersonQuery] = useState('')
  const [personSuggestions, setPersonSuggestions] = useState<PersonSearchResultDto[]>([])
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResultDto | null>(null)
  const personTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* STATE – Pferd */
  const [horseQuery, setHorseQuery] = useState('')
  const [horseSuggestions, setHorseSuggestions] = useState<HorseSearchResultDto[]>([])
  const [selectedHorse, setSelectedHorse] = useState<HorseSearchResultDto | null>(null)
  const horseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  /* LOGIC – Show auswählen */
  const selectShow = useCallback((show: ShowDto | null) => {
    setSelectedShow(show)
    setSelectedExam(null)
    setParticipants([])
    setExams([])
    setAllStarters([])
    setScores([])
    setScoresType(null)
    setScoresExpanded(false)
    setExamSchedule([])
    setExamScheduleSortCol('zeit' as const)
    if (show?.id != null) {
      setExamsLoading(true)
      new ExamApi(authState.apicfg())
        .apiExamByShowShowIdGet({ showId: show.id })
        .then(data => setExams([...data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))))
        .catch(() => setExams([]))
        .finally(() => setExamsLoading(false))
      setAllStartersLoading(true)
      new StartersApi(authState.apicfg())
        .apiStartersShowShowIdStarterGet({ showId: show.id })
        .then(data => setAllStarters([...data].sort((a, b) => (a.startNo ?? 0) - (b.startNo ?? 0))))
        .catch(() => setAllStarters([]))
        .finally(() => setAllStartersLoading(false))
      setExamScheduleLoading(true)
      new ExamApi(authState.apicfg())
        .apiExamScheduleGet({ showId: show.id })
        .then(data => setExamSchedule(data))
        .catch(() => setExamSchedule([]))
        .finally(() => setExamScheduleLoading(false))
    }
  }, [])

  /* LOGIC – alle Shows laden */
  useEffect(() => {
    setShowsLoading(true)
    new ShowApi(authState.apicfg())
      .apiShowGetRaw()
      .then(r => r.raw.json())
      .then((shows: ShowDto[]) => {
        const sorted = [...shows].sort((a, b) => {
          const da = a.startDate ? new Date(a.startDate).getTime() : 0
          const db = b.startDate ? new Date(b.startDate).getTime() : 0
          return db - da
        })
        setShows(sorted)
        const autoLoadedShow = sorted.find((show) => show.autoLoad === true)
        selectShow(autoLoadedShow ?? null)
      })
      .catch(() => setShows([]))
      .finally(() => setShowsLoading(false))
  }, [])

  /* LOGIC – Teilnehmer laden wenn Prüfung gewählt */
  const loadParticipants = useCallback((exam: ExamDto | null) => {
    setParticipants([])
    setSortMode('startingOrder')
    if (exam?.id == null) return
    setParticipantsLoading(true)
    new ExamApi(authState.apicfg())
      .apiExamExamIdDetailsGet({ examId: exam.id })
      .then(data => {
        const parts = data.participants ?? []
        setParticipants(parts)
        if (parts.some((p: ExamParticipantDto) => (p.place ?? 0) !== 0)) setSortMode('place')
      })
      .catch(() => setParticipants([]))
      .finally(() => setParticipantsLoading(false))
  }, [])

  /* LOGIC – Person search */
  const searchPersons = useCallback((e: AutoCompleteCompleteEvent) => {
    const q = e.query?.trim()
    if (personTimer.current) clearTimeout(personTimer.current)
    if (!q || q.length < 2) { setPersonSuggestions([]); return }
    personTimer.current = setTimeout(() => {
      new PersonApi(authState.apicfg())
        .apiPersonSearchGet({ q })
        .then(r => setPersonSuggestions(r.items ?? []))
        .catch(() => setPersonSuggestions([]))
    }, 300)
  }, [])

  /* LOGIC – Horse search */
  const searchHorses = useCallback((e: AutoCompleteCompleteEvent) => {
    const q = e.query?.trim()
    if (horseTimer.current) clearTimeout(horseTimer.current)
    if (!q || q.length < 2) { setHorseSuggestions([]); return }
    horseTimer.current = setTimeout(() => {
      new HorseApi(authState.apicfg())
        .apiHorseSearchGet({ q })
        .then(r => setHorseSuggestions(r.items ?? []))
        .catch(() => setHorseSuggestions([]))
    }, 300)
  }, [])

  /* DERIVED – sortierte Teilnehmer (place=0 ausgeblendet) */
  const sortedParticipants = useMemo(() =>
    [...participants]
      .filter(p => sortMode === 'place' ? (p.place ?? 0) !== 0 : true)
      .sort((a, b) => {
        if (sortMode === 'place') {
          const pa = a.place ?? 9999
          const pb = b.place ?? 9999
          return pa !== pb ? pa - pb : (a.startNo ?? 0) - (b.startNo ?? 0)
        }
        return (a.startingOrder ?? a.startNo ?? 0) - (b.startingOrder ?? b.startNo ?? 0)
      })
  , [participants, sortMode])

  const startingOrderUndefined = useMemo(() =>
    sortMode === 'startingOrder' && participants.length > 0 &&
    participants.every(p => (p.startingOrder ?? 0) === 0)
  , [participants, sortMode])

  const placeUndefined = useMemo(() =>
    participants.length > 0 && participants.every(p => (p.place ?? 0) === 0)
  , [participants])

  /* DERIVED – sortierter Prüfungsplan */
  const DAY_ORDER: Record<string, number> = { mo: 0, di: 1, mi: 2, do: 3, fr: 4, sa: 5, so: 6 }
  const parseScheduleTime = (linkText: string | null | undefined): number => {
    if (!linkText) return Infinity
    const lower = linkText.toLowerCase()
    const dayMatch = lower.match(/^([a-z]{2})\s/)
    const dayOffset = dayMatch ? (DAY_ORDER[dayMatch[1]] ?? 0) : 0
    const timeMatch = linkText.match(/(\d{1,2}):(\d{2})/)
    if (!timeMatch) return Infinity
    return dayOffset * 24 * 60 + parseInt(timeMatch[1]) * 60 + parseInt(timeMatch[2])
  }

  const sortedExamSchedule = useMemo(() =>
    [...examSchedule].sort((a, b) => (a.examNo ?? 0) - (b.examNo ?? 0))
  , [examSchedule])

  // Flache Liste für Zeitsortierung: jede linkedExam-Zeile als eigener Eintrag
  const timeScheduleRows = useMemo(() => {
    const TYPE_LABEL: Record<number, string> = { 0: 'Kombi', 1: 'VE', 2: 'BF', 3: 'AF' }
    const rows: { linkText: string | null; venue: string | null; examId: number | null; examNo: number | null; examName: string | null; scoringTypeName: string | null; countRider: number | null; typeLabel: string; time: number }[] = []
    for (const ex of examSchedule) {
      for (const d of ex.linkedExams ?? []) {
        rows.push({
          linkText: d.linkText ?? null,
          venue: d.venue ?? null,
          examId: d.examId ?? null,
          examNo: ex.examNo ?? null,
          examName: ex.examName ?? null,
          scoringTypeName: ex.scoringTypeName ?? null,
          countRider: ex.countRider ?? null,
          typeLabel: TYPE_LABEL[d.examDisplayType ?? -1] ?? '',
          time: parseScheduleTime(d.linkText),
        })
      }
    }
    rows.sort((a, b) => a.time !== b.time ? a.time - b.time : (a.examNo ?? 0) - (b.examNo ?? 0))
    return rows
  }, [examSchedule])

  const handleScheduleSort = useCallback((col: 'nr' | 'zeit') => {
    setExamScheduleSortCol(col === 'nr' ? 'zeit' : 'nr_asc')
  }, [])

  const openExamOverlay = useCallback((examId: number, examName?: string | null) => {
    setExamOverlay({ examId, examName, showName: selectedShow?.name })
  }, [selectedShow])

  /* LOGIC – aus Overlay zu einer Prüfung navigieren */
  const navigateToExam = useCallback((showId: number, examId: number) => {
    setOverlay(null)
    const exam = exams.find(e => e.id === examId)
    openExamOverlay(examId, exam?.name)
  }, [exams, openExamOverlay])



  /* TEMPLATE */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', maxWidth: '100%' }}>

      {/* Turnier */}
      <div>
        <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
          Bitte wählen Sie das Turnier, zu dem Sie weitere Informationen erhalten möchten.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Dropdown
            value={selectedShow}
            options={shows}
            optionLabel='name'
            placeholder={showsLoading ? 'Wird geladen \u2026' : 'Turnier ausw\u00e4hlen'}
            filter
            filterBy='name'
            onChange={(e) => selectShow(e.value)}
            style={{ flex: 1 }}
            scrollHeight='380px'
            itemTemplate={(show: ShowDto) => {
              const fmt = (d: Date | null | undefined) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null
              const von = fmt(show.startDate)
              const bis = fmt(show.endDate)
              const dates = von && bis && von !== bis ? `${von} – ${bis}` : von ?? bis
              return <span>{show.name}{dates ? ` (${dates})` : ''}{show.city ? ` – ${show.city}` : ''}</span>
            }}
            disabled={showsLoading}
          />
          {selectedShow && (
            <button
              onClick={() => { setSelectedShow(null); setSelectedExam(null); setParticipants([]); setExams([]); setAllStarters([]) }}
              title='Turnier leeren'
              style={{ flexShrink: 0, background: 'none', border: '1px solid #ccc',
                       borderRadius: '4px', cursor: 'pointer', fontSize: '1rem',
                       padding: '0.2rem 0.5rem', lineHeight: 1, color: '#666' }}
            >
              ✕
            </button>
          )}
        </div>
        {selectedShow && (
          <>
            {/* Gesamtwertung */}
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.5rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.88rem', marginRight: '0.2rem' }}>Gesamtwertung:</span>
              {([{ label: 'Sport', type: 1 }, { label: 'Freizeit', type: 2 }, { label: 'Jugend A', type: 3 }, { label: 'Jugend B', type: 4 }] as const).map(({ label, type }) => (
                <button
                  key={type}
                  onClick={() => {
                    setScoresType(type)
                    setScores([])
                    setScoresExpanded(false)
                    if (selectedShow?.id == null) return
                    setScoresLoading(true)
                    new ShowApi(authState.apicfg())
                      .apiShowScoresGet({ showId: selectedShow.id, scoringType: type })
                      .then(data => setScores(data))
                      .catch(() => setScores([]))
                      .finally(() => setScoresLoading(false))
                  }}
                  style={{
                    padding: '0.25rem 0.7rem', fontSize: '0.82rem', cursor: 'pointer',
                    border: '1px solid #bbb', borderRadius: '4px',
                    background: scoresType === type ? '#1976d2' : '#f5f5f5',
                    color: scoresType === type ? '#fff' : '#333',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Gesamtwertungs-Tabelle */}
            {scoresType !== null && (
              <div style={{ marginTop: '0.5rem', overflowX: 'auto' }}>
                {scoresLoading && <div style={{ color: '#888', fontSize: '0.88rem' }}>Wird geladen …</div>}
                {!scoresLoading && scores.length === 0 && <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Daten gefunden.</div>}
                {!scoresLoading && scores.length > 0 && (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '320px' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                          <th style={{ padding: '0.2rem 0.3rem' }}>Pl.</th>
                          <th style={{ padding: '0.2rem 0.3rem' }}>St.Nr.</th>
                          <th style={{ padding: '0.2rem 0.3rem' }}>Pferd</th>
                          <th style={{ padding: '0.2rem 0.3rem' }}>Rasse</th>
                          <th style={{ padding: '0.2rem 0.3rem' }}>Reiter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(scoresExpanded ? scores : scores.slice(0, 5)).map((s, idx) => {
                          if (s.totalScore == null) return null
                          const riderName = [s.riderFirstName, s.riderLastName].filter(Boolean).join(' ')
                          const details: import('../../../api').ShowScoreDetailDto[] = s.details ?? []
                          return (
                            <React.Fragment key={idx}>
                              {/* Zeile 1: Pl. | Pferd | Rasse | Reiter */}
                              <tr style={{ borderTop: idx > 0 ? '1px solid #e8e8e8' : undefined }}>
                                <td style={{ padding: '0.25rem 0.3rem', fontWeight: 600, verticalAlign: 'top' }}>
                                  {s.place ?? '–'}
                                </td>
                                <td style={{ padding: '0.25rem 0.3rem', color: '#666', verticalAlign: 'top' }}>
                                  {s.startNo ?? '–'}
                                </td>
                                <td style={{ padding: '0.25rem 0.3rem', fontWeight: 500, verticalAlign: 'top' }}>
                                  {s.horseRegisterId
                                    ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.horseRegisterId!, mode: 'horse', name: s.horseName ?? s.horseRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>{s.horseName ?? '–'}</a>
                                    : (s.horseName ?? '–')}
                                </td>
                                <td style={{ padding: '0.25rem 0.3rem', color: '#666', fontSize: '0.82rem', verticalAlign: 'top' }}>
                                  {s.horseBreed ?? ''}
                                </td>
                                <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'top' }}>
                                  {s.riderRegisterId
                                    ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.riderRegisterId!, mode: 'rider', name: riderName || s.riderRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>{riderName || '–'}</a>
                                    : (riderName || '–')}
                                </td>
                              </tr>
                              {/* Zeile 2: Gesamtpunkte – Einzelpunkte je Prüfung */}
                              <tr>
                                <td style={{ padding: '0.05rem 0.3rem 0.4rem' }} />
                                <td style={{ padding: '0.05rem 0.3rem 0.4rem' }} />
                                <td colSpan={3} style={{ padding: '0.05rem 0.3rem 0.4rem', fontSize: '0.82rem', color: '#444' }}>
                                  {s.totalScore != null && (
                                    <span style={{ fontWeight: 600, marginRight: '0.5rem' }}>{Math.round(Number(s.totalScore))}</span>
                                  )}
                                  {s.totalScore != null && details.filter(d => (d.points ?? 0) !== 0).length > 0 && (
                                    <span style={{ marginRight: '0.5rem', color: '#bbb' }}>––</span>
                                  )}
                                  {details.map((d, di) => {
                                    if (!d.points) return null
                                    const isStreichnote = (d.calc ?? -1) === 0 && d.points > 0
                                    const tooltip = d.examName ?? 'Zur Prüfung'
                                    const inner = (
                                      <span style={{ textDecoration: isStreichnote ? 'line-through' : undefined, color: isStreichnote ? '#aaa' : undefined }}>
                                        {Math.round(d.points)}
                                      </span>
                                    )
                                    return (
                                      <span key={di} style={{ marginRight: '0.4rem', display: 'inline-block' }}>
                                        {d.examId != null && selectedShow?.id != null
                                          ? <a href='#' title={tooltip} onClick={(e) => { e.preventDefault(); openExamOverlay(d.examId!, d.examName) }} style={{ textDecoration: 'none', color: isStreichnote ? '#aaa' : '#1976d2' }}>{inner}</a>
                                          : inner}
                                      </span>
                                    )
                                  })}
                                </td>
                              </tr>
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                    </table>
                    {scores.length > 5 && (
                      <div style={{ marginTop: '0.4rem' }}>
                        <a
                          href='#'
                          onClick={(e) => { e.preventDefault(); setScoresExpanded(v => !v) }}
                          style={{ fontSize: '0.82rem', color: '#1976d2', textDecoration: 'none' }}
                        >
                          {scoresExpanded ? '▲ weniger anzeigen' : `»»» mehr anzeigen (${scores.length - 5} weitere)`}
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', borderBottom: '2px solid #ddd' }}>
              {(['exams', 'starters'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => { setScheduleTab(tab); if (tab !== 'exams') { setSelectedExam(null); setParticipants([]) } }}
                  style={{
                    padding: '0.3rem 1rem', fontSize: '0.88rem', cursor: 'pointer',
                    border: 'none', borderBottom: scheduleTab === tab ? '2px solid #1976d2' : '2px solid transparent',
                    background: 'none', fontWeight: scheduleTab === tab ? 700 : 400,
                    color: scheduleTab === tab ? '#1976d2' : '#555', marginBottom: '-2px'
                  }}
                >
                  {tab === 'exams' ? 'Prüfungen' : 'Starter'}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Tab Prüfungen */}
      {selectedShow && scheduleTab === 'exams' && (
        <div style={{ overflowX: 'auto' }}>
          {examScheduleLoading && <div style={{ color: '#888', fontSize: '0.88rem' }}>Wird geladen …</div>}
          {!examScheduleLoading && examSchedule.length === 0 && <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Prüfungen gefunden.</div>}
          {!examScheduleLoading && examSchedule.length > 0 && (
            <>
              {/* Nr-Ansicht */}
              {examScheduleSortCol !== 'zeit' && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '500px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                      <th style={{ padding: '0.2rem 0.3rem', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: '#1976d2' }}
                        onClick={() => handleScheduleSort('nr')}>
                        Nr ▲
                      </th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>Prüfung</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>Typ</th>
                      <th style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>Anzahl</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>Kombi</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>VE</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>BF</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>AF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedExamSchedule.map((ex, idx) => {
                      const detailByType = (type: number) => ex.linkedExams?.find((d: {examDisplayType?: number|null}) => d.examDisplayType === type)
                      const detailLink = (type: number) => {
                        const d = detailByType(type)
                        if (!d?.linkText) return null
                        return d.examId != null
                          ? <a href='#' onClick={(e2) => { e2.preventDefault(); openExamOverlay(d.examId!, ex.examName) }} style={{ color: '#1976d2', textDecoration: 'none' }}>{d.linkText}</a>
                          : <span>{d.linkText}</span>
                      }
                      return (
                        <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : undefined }}>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{ex.examNo ?? '–'}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{ex.examName ?? '–'}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{ex.scoringTypeName ?? ''}</td>
                          <td style={{ padding: '0.25rem 0.3rem', textAlign: 'right' }}>{ex.countRider ?? ''}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{detailLink(0)}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{detailLink(1)}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{detailLink(2)}</td>
                          <td style={{ padding: '0.25rem 0.3rem' }}>{detailLink(3)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              {/* Zeit-Ansicht */}
              {examScheduleSortCol === 'zeit' && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '400px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                      <th style={{ padding: '0.2rem 0.3rem', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', color: '#1976d2' }}
                        onClick={() => handleScheduleSort('zeit')}>
                        Zeit ▲
                      </th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>Prüfung</th>
                      <th style={{ padding: '0.2rem 0.3rem' }}>Typ</th>
                      <th style={{ padding: '0.2rem 0.3rem', textAlign: 'right' }}>Anzahl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeScheduleRows.map((row, idx) => (
                      <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : undefined }}>
                        <td style={{ padding: '0.25rem 0.3rem', whiteSpace: 'nowrap' }}>
                          {row.examId != null
                            ? <a href='#' onClick={(e2) => { e2.preventDefault(); openExamOverlay(row.examId!, row.examName) }} style={{ color: '#1976d2', textDecoration: 'none' }}>{row.linkText ?? '–'}</a>
                            : (row.linkText ?? '–')}
                          {row.venue && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.1rem' }}>{row.venue}</div>}
                        </td>
                        <td style={{ padding: '0.25rem 0.3rem' }}>
                          {row.examName ?? '–'}
                          {row.typeLabel && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.1rem' }}>{row.typeLabel}</div>}
                        </td>
                        <td style={{ padding: '0.25rem 0.3rem' }}>{row.scoringTypeName ?? ''}</td>
                        <td style={{ padding: '0.25rem 0.3rem', textAlign: 'right' }}>
                          {row.typeLabel === 'BF' || row.typeLabel === 'AF' ? 0 : (row.countRider ?? '')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}

      {/* Tab Starter */}
      {selectedShow && scheduleTab === 'starters' && (
        <div>
          {allStartersLoading && <div style={{ color: '#888' }}>Wird geladen &#8230;</div>}
          {!allStartersLoading && allStarters.length > 0 && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '360px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                    <th style={{ padding: '0.2rem 0.3rem' }}>St.Nr.</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Pferd</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Reiter</th>
                    <th style={{ padding: '0.2rem 0.3rem' }}>Prüfungen</th>
                  </tr>
                </thead>
                <tbody>
                  {allStarters.map((s, idx) => (
                    <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid #eee' : undefined }}>
                      <td style={{ padding: '0.25rem 0.3rem' }}>{s.startNo ?? '\u2013'}</td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {s.horseRegisterId
                          ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.horseRegisterId!, mode: 'horse', name: s.horseName ?? s.horseRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>{s.horseName ?? '\u2013'}</a>
                          : (s.horseName ?? '\u2013')}
                        {s.scoringTypeName && <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.1rem' }}>{s.scoringTypeName}</div>}
                      </td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {s.riderRegisterId
                          ? <a href='#' onClick={(e) => { e.preventDefault(); const n = [s.riderFirstName, s.riderLastName].filter(Boolean).join(' '); openOverlay({ registerId: s.riderRegisterId!, mode: 'rider', name: n || s.riderRegisterId!, showId: selectedShow?.id, showName: selectedShow?.name }) }}>{[s.riderFirstName, s.riderLastName].filter(Boolean).join(' ') || '\u2013'}</a>
                          : ([s.riderFirstName, s.riderLastName].filter(Boolean).join(' ') || '\u2013')}
                      </td>
                      <td style={{ padding: '0.25rem 0.3rem' }}>
                        {(s.exams ?? []).map((ex, ei) => (
                          <span key={ei}
                            onClick={() => { if (ex.examId != null) openExamOverlay(ex.examId, ex.examName) }}
                            style={{ display: 'inline-block', marginRight: '0.3rem', marginBottom: '0.2rem', padding: '0.1rem 0.4rem', border: '1px solid #bbb', borderRadius: '3px', fontSize: '0.78rem', cursor: 'pointer', background: '#f5f5f5', color: '#1976d2' }}
                            title={ex.examName ?? undefined}>
                            {ex.examName ?? ex.examId}{ex.type ? ` [${ex.type}]` : ''}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!allStartersLoading && allStarters.length === 0 && (
            <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Starter gefunden.</div>
          )}
        </div>
      )}

      {/* Person / Reiter */}
      {!selectedShow && (
        <div>
          <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
            Hier können Sie in allen IGV registrierten Reitern suchen.
          </div>
          <AutoComplete
            value={selectedPerson ?? personQuery}
            suggestions={personSuggestions}
            completeMethod={searchPersons}
            field='lastName'
            placeholder='Reiter suchen'
            onChange={(e) => {
              if (typeof e.value === 'string') { setPersonQuery(e.value); setSelectedPerson(null) }
              else {
                setSelectedPerson(e.value)
                if (e.value?.registerId) openOverlay({ registerId: e.value.registerId, mode: 'rider', name: [e.value.lastName, e.value.firstName].filter(Boolean).join(', ') || e.value.registerId })
              }
            }}
            style={{ width: '100%' }}
            scrollHeight='380px'
            itemTemplate={(person: PersonSearchResultDto) => (
              <span>{person.lastName}, {person.firstName}</span>
            )}
          />
        </div>
      )}

      {/* Pferd */}
      {!selectedShow && (
        <div>
          <div style={{ fontSize: '0.88rem', color: '#555', marginBottom: '0.4rem' }}>
            Hier können Sie in allen IGV registrierten Pferden suchen.
          </div>
          <AutoComplete
            value={selectedHorse ?? horseQuery}
            suggestions={horseSuggestions}
            completeMethod={searchHorses}
            field='name'
            placeholder='Pferd suchen'
            onChange={(e) => {
              if (typeof e.value === 'string') { setHorseQuery(e.value); setSelectedHorse(null) }
              else {
                setSelectedHorse(e.value)
                if (e.value?.registerId) openOverlay({ registerId: e.value.registerId, mode: 'horse', name: e.value.name ?? e.value.registerId })
              }
            }}
            style={{ width: '100%' }}
            scrollHeight='380px'
            itemTemplate={(horse: HorseSearchResultDto) => (
              <span>{horse.name}{horse.breed ? ` (${horse.breed})` : ''}</span>
            )}
                />
              </div>
            )}

          {/* Jahreswertung */}
          {!selectedShow && (
            <div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.4rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.88rem', marginRight: '0.2rem' }}>Jahreswertung:</span>
                <Dropdown
                  value={selectedYear}
                  options={yearOptions}
                  onChange={(e) => {
                    setSelectedYear(e.value)
                    setYearScores([])
                    setYearScoresType(null)
                    setYearScoresExpanded(false)
                  }}
                  style={{ width: '7rem' }}
                  scrollHeight='300px'
                />
                {([{ label: 'Sport', type: 1 }, { label: 'Freizeit', type: 2 }, { label: 'Jugend A', type: 3 }, { label: 'Jugend B', type: 4 }] as const).map(({ label, type }) => (
                  <button
                    key={type}
                    onClick={() => {
                      setYearScoresType(type)
                      setYearScores([])
                      setYearScoresExpanded(false)
                      setYearScoresLoading(true)
                      new ShowApi(authState.apicfg())
                        .apiShowYearHistoryGet({ year: selectedYear, scoringType: type })
                        .then(data => setYearScores(data))
                        .catch(() => setYearScores([]))
                        .finally(() => setYearScoresLoading(false))
                    }}
                    style={{
                      padding: '0.25rem 0.7rem', fontSize: '0.82rem', cursor: 'pointer',
                      border: '1px solid #bbb', borderRadius: '4px',
                      background: yearScoresType === type ? '#1976d2' : '#f5f5f5',
                      color: yearScoresType === type ? '#fff' : '#333',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {yearScoresType !== null && (
                <div style={{ overflowX: 'auto' }}>
                  {yearScoresLoading && <div style={{ color: '#888', fontSize: '0.88rem' }}>Wird geladen …</div>}
                  {!yearScoresLoading && yearScores.length === 0 && <div style={{ color: '#888', fontSize: '0.88rem' }}>Keine Daten gefunden.</div>}
                  {!yearScoresLoading && yearScores.length > 0 && (
                    <>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '320px' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                            <th style={{ padding: '0.2rem 0.3rem' }}>Pl.</th>
                            <th style={{ padding: '0.2rem 0.3rem' }}>Pferd</th>
                            <th style={{ padding: '0.2rem 0.3rem' }}>Rasse</th>
                            <th style={{ padding: '0.2rem 0.3rem' }}>Reiter</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(yearScoresExpanded ? yearScores : yearScores.slice(0, 5)).map((s, idx) => {
                            if (s.totalScore == null) return null
                            const riderName = [s.riderFirstName, s.riderLastName].filter(Boolean).join(' ')
                            const details: import('../../../api').ShowScoreDetailDto[] = s.details ?? []
                            return (
                              <React.Fragment key={idx}>
                                <tr style={{ borderTop: idx > 0 ? '1px solid #e8e8e8' : undefined }}>
                                  <td style={{ padding: '0.25rem 0.3rem', fontWeight: 600, verticalAlign: 'top' }}>{s.place ?? '\u2013'}</td>
                                  <td style={{ padding: '0.25rem 0.3rem', fontWeight: 500, verticalAlign: 'top' }}>
                                    {s.horseRegisterId
                                      ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.horseRegisterId!, mode: 'horse', name: s.horseName ?? s.horseRegisterId! }) }}>{s.horseName ?? '\u2013'}</a>
                                      : (s.horseName ?? '\u2013')}
                                  </td>
                                  <td style={{ padding: '0.25rem 0.3rem', color: '#666', fontSize: '0.82rem', verticalAlign: 'top' }}>{s.horseBreed ?? ''}</td>
                                  <td style={{ padding: '0.25rem 0.3rem', verticalAlign: 'top' }}>
                                    {s.riderRegisterId
                                      ? <a href='#' onClick={(e) => { e.preventDefault(); openOverlay({ registerId: s.riderRegisterId!, mode: 'rider', name: riderName || s.riderRegisterId! }) }}>{riderName || '\u2013'}</a>
                                      : (riderName || '\u2013')}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '0.05rem 0.3rem 0.4rem' }} />
                                  <td colSpan={3} style={{ padding: '0.05rem 0.3rem 0.4rem', fontSize: '0.82rem', color: '#444' }}>
                                    {s.totalScore != null && (
                                      <span style={{ fontWeight: 600, marginRight: '0.5rem' }}>{Math.round(Number(s.totalScore))}</span>
                                    )}
                                    {s.totalScore != null && details.filter((d) => (d.points ?? 0) !== 0).length > 0 && (
                                      <span style={{ marginRight: '0.5rem', color: '#bbb' }}>––</span>
                                    )}
                                    {details.map((d, di) => {
                                      if (!d.points) return null
                                      const isStreichnote = (d.calc ?? -1) === 0 && d.points > 0
                                      const fmt = d.showDate ? new Date(d.showDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null
                                      const tooltip = [d.showName, fmt].filter(Boolean).join(' – ')
                                      const inner = (
                                        <span style={{ textDecoration: isStreichnote ? 'line-through' : undefined, color: isStreichnote ? '#aaa' : undefined }}>
                                          {Math.round(d.points)}
                                        </span>
                                      )
                                      return (
                                        <span key={di} style={{ marginRight: '0.4rem', display: 'inline-block' }}>
                                          {d.showId != null
                                            ? <a href='#' title={tooltip || 'Zum Turnier'} onClick={(e) => { e.preventDefault(); openOverlay({ registerId: '', mode: 'show', name: d.showName ?? 'Turnier', showId: d.showId, showName: d.showName ?? undefined, horseRegisterId: s.horseRegisterId, riderRegisterId: s.riderRegisterId }) }} style={{ textDecoration: 'none', color: isStreichnote ? '#aaa' : '#1976d2' }}>{inner}</a>
                                            : inner}
                                        </span>
                                      )
                                    })}
                                  </td>
                                </tr>
                              </React.Fragment>
                            )
                          })}
                        </tbody>
                      </table>
                      {yearScores.filter(s => s.totalScore != null).length > 5 && (
                        <div style={{ marginTop: '0.4rem' }}>
                          <a href='#' onClick={(e) => { e.preventDefault(); setYearScoresExpanded(v => !v) }}
                            style={{ fontSize: '0.82rem', color: '#1976d2', textDecoration: 'none' }}>
                            {yearScoresExpanded ? '▲ weniger anzeigen' : `»»» mehr anzeigen (${yearScores.filter(s => s.totalScore != null).length - 5} weitere)`}
                          </a>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {examOverlay && (
            <ExamParticipantsOverlay
              examId={examOverlay.examId}
              examName={examOverlay.examName}
              showName={examOverlay.showName}
              onHide={() => setExamOverlay(null)}
              onOpenOther={(registerId, mode, name) => { setExamOverlay(null); openOverlay({ registerId, mode, name, showId: selectedShow?.id, showName: selectedShow?.name }) }}
              onOpenExam={(eid, eName) => { setExamOverlay({ examId: eid, examName: eName, showName: selectedShow?.name }) }}
            />
          )}
          {overlay && (
        <StarterHistoryOverlay
          registerId={overlay!.registerId}
          mode={overlay!.mode}
          name={overlay!.name}
          showId={overlay!.showId}
          showName={overlay!.showName}
          horseRegisterId={overlay!.horseRegisterId}
          riderRegisterId={overlay!.riderRegisterId}
          onHide={closeOverlay}
          onNavigateToExam={navigateToExam}
          onOpenOther={(regId, m, n) => openOverlay({ registerId: regId, mode: m, name: n })}/>
      )}
    </div>
  )
}

